import { api } from "./client";
import type { RequestOptionsJson } from "../shared/webauthn";
import type { AuthContext, ClientBranding, UserOut } from "./types";

export interface LoginResult {
  user: UserOut;
  return_to: string | null;
}

export interface MfaChallenge {
  mfa_required: true;
  mfa_token: string;
}

export function isMfaChallenge(
  result: LoginResult | MfaChallenge,
): result is MfaChallenge {
  return (result as MfaChallenge).mfa_required === true;
}

export const authApi = {
  context: () => api<AuthContext>("/auth/context"),
  clientBranding: (clientId: string) =>
    api<ClientBranding>(`/clients/${encodeURIComponent(clientId)}/public`),
  register: (body: {
    email: string;
    password: string;
    first_name: string;
    last_name?: string;
    country?: string;
    turnstile_token?: string;
    return_to?: string;
  }) => api<LoginResult>("/auth/register", { method: "POST", body }),
  loginPassword: (body: {
    email: string;
    password: string;
    turnstile_token?: string;
    return_to?: string;
  }) =>
    api<LoginResult | MfaChallenge>("/auth/login/password", {
      method: "POST",
      body,
    }),
  loginMfa: (body: { mfa_token: string; code: string }) =>
    api<LoginResult>("/auth/login/mfa", { method: "POST", body }),
  passkeyOptions: (email?: string) =>
    api<RequestOptionsJson>("/auth/login/passkey/options", {
      method: "POST",
      body: email ? { email } : {},
    }),
  loginPasskey: (body: {
    credential: Record<string, unknown>;
    return_to?: string;
  }) => api<LoginResult>("/auth/login/passkey", { method: "POST", body }),
  mfaPasskeyOptions: (mfa_token: string) =>
    api<RequestOptionsJson>("/auth/login/mfa/passkey/options", {
      method: "POST",
      body: { mfa_token },
    }),
  loginMfaPasskey: (body: {
    mfa_token: string;
    credential: Record<string, unknown>;
  }) => api<LoginResult>("/auth/login/mfa/passkey", { method: "POST", body }),
  logout: () => api<void>("/auth/logout", { method: "POST" }),
  sendVerification: () =>
    api<{ sent: boolean }>("/auth/email-verification/send", { method: "POST" }),
  confirmVerification: (token: string) =>
    api<{ user: UserOut }>("/auth/email-verification/confirm", {
      method: "POST",
      body: { token },
    }),
  requestReset: (body: { email: string; turnstile_token?: string }) =>
    api<{ accepted: boolean }>("/auth/password-reset/request", {
      method: "POST",
      body,
    }),
  confirmReset: (body: { token: string; new_password: string }) =>
    api<{ changed: boolean }>("/auth/password-reset/confirm", {
      method: "POST",
      body,
    }),
  consent: (body: {
    client_id: string;
    scope: string;
    return_to: string;
    decision: "allow" | "deny";
  }) =>
    api<{ redirect_to: string | null }>("/auth/consent", {
      method: "POST",
      body,
    }),
};

export function googleStartUrl(
  apiUrl: string,
  returnTo: string | null,
  link = false,
): string {
  const params = new URLSearchParams();
  if (returnTo) params.set("return_to", returnTo);
  if (link) params.set("link", "1");
  const query = params.toString();
  return `${apiUrl}/auth/google/start${query ? `?${query}` : ""}`;
}

export type ExternalProvider = "google" | "facebook" | "apple";

export const PROVIDER_LABELS: Record<ExternalProvider, string> = {
  google: "Google",
  facebook: "Facebook",
  apple: "Apple",
};

/** `/auth/<provider>/start` (docs/phase6/P6-T03); Google keeps its dedicated route. */
export function providerStartUrl(
  apiUrl: string,
  provider: ExternalProvider,
  returnTo: string | null,
  link = false,
): string {
  const params = new URLSearchParams();
  if (returnTo) params.set("return_to", returnTo);
  if (link) params.set("link", "1");
  const query = params.toString();
  return `${apiUrl}/auth/${provider}/start${query ? `?${query}` : ""}`;
}
